import { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { FieldArrayComponent } from 'components/_basic/Form/FieldArray';
import { TextInputControl } from 'components/_basic/Form/TextInput';
import { FormHeader } from 'components/_basic/FormHeader/FormHeader';
import { FormSectionCard } from 'components/_basic/FormSectionCard/FormSectionCard';
import { FORM_SECTION_CARD_VARIANT } from 'components/_basic/FormSectionCard/types';

import { concatWithDot } from 'helpers/string';

import { PremisesInformation } from './PremisesInformation';
import { LOCATION_AND_PREMISES_NAMES, prefix, premisesInformationValue } from './utils/form';

export const LocationAndPremises: FC = () => {
  const { t } = useTranslation();
  const { control } = useFormContext();

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.BUSINESS_OPERATIONS,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_BUSINESS_OPERATIONS.LOCATION_AND_PREMISES,
  );

  return (
    <>
      <FormSectionCard variant={FORM_SECTION_CARD_VARIANT.LARGE}>
        <FormHeader
          locked={!canAccess}
          title={t('businessOperations.title')}
          badgeText={t('businessOperations.location.title')}
          section={FOR_BANK_SECTIONS.BUSINESS_OPERATIONS}
        />
      </FormSectionCard>
      {/* 6.1.1 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('businessOperations.location.currentLocation')}
        aiFieldName={concatWithDot(prefix, LOCATION_AND_PREMISES_NAMES.LOCATION)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, LOCATION_AND_PREMISES_NAMES.LOCATION)}
          name={concatWithDot(prefix, LOCATION_AND_PREMISES_NAMES.LOCATION)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 6.1.2 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('businessOperations.location.detailedInformation.title')}
        collapsible
      >
        <FieldArrayComponent
          name={concatWithDot(prefix, LOCATION_AND_PREMISES_NAMES.PREMISES_INFORMATION)}
          initialItem={premisesInformationValue}
          buttonText={t('businessOperations.location.detailedInformation.buttonText')}
          disabled={!canAccess}
        >
          {(namePrefix) => <PremisesInformation name={namePrefix} disabled={!canAccess} />}
        </FieldArrayComponent>
      </FormSectionCard>
      {/* 6.1.3 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('businessOperations.location.qualities')}
        aiFieldName={concatWithDot(prefix, LOCATION_AND_PREMISES_NAMES.QUALITIES)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, LOCATION_AND_PREMISES_NAMES.QUALITIES)}
          name={concatWithDot(prefix, LOCATION_AND_PREMISES_NAMES.QUALITIES)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 6.1.4 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('businessOperations.location.futurePlans')}
        aiFieldName={concatWithDot(prefix, LOCATION_AND_PREMISES_NAMES.FUTURE_PLANS)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, LOCATION_AND_PREMISES_NAMES.FUTURE_PLANS)}
          name={concatWithDot(prefix, LOCATION_AND_PREMISES_NAMES.FUTURE_PLANS)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
    </>
  );
};
